import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const JOB_LABELS = {
  Manager:         'Manager',
  Cashier:         'Cashier',
  ComputerOperator: 'Computer Operator',
  CashierAndOperator: 'Cashier and Operator',
  Labors:          'Labors',
  FamilyRepresentative: 'Family Representative',
};

const EMPTY = {
  job_type: '', name: '', address: '', married: '', sex: '',
  religion: '', birth_date: '', phone: '', id_no: '', education: '',
  epf_no: '', etf_no: '', family_details: '', working_history: '',
  blacklisted: false, stay_overnight: false, working_history_outside: '',
};

// ── Comment form (single comment: Date / Description / Image / Decision) ──
function CommentForm({ initial, onSave, onCancel }) {
  const [date, setDate]               = useState(initial?.date ? initial.date.split('T')[0] : '');
  const [description, setDescription] = useState(initial?.description || '');
  const [decision, setDecision]       = useState(initial?.decision || 'good');
  const [imageFile, setImageFile]     = useState(null);
  const [saving, setSaving]           = useState(false);

  const submit = async () => {
    if (!date) { toast.error('Date is required'); return; }
    setSaving(true);
    try { await onSave({ date, description, decision, imageFile }); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ padding: 12, marginBottom: 12 }}>
      <div className="grid-3">
        <div className="form-group">
          <label className="form-label">Date</label>
          <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Decision</label>
          <select className="form-select" value={decision} onChange={e => setDecision(e.target.value)}>
            <option value="good">Good</option>
            <option value="bad">Bad</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Image</label>
          <input
            className="form-input"
            type="file"
            accept="image/*"
            onChange={e => setImageFile(e.target.files[0] || null)}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-textarea"
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : 'Save Comment'}
        </button>
      </div>
    </div>
  );
}

// ── Comments list + add/edit/delete (replaces the old single comment field) ──
function EmployeeComments({ employeeId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [adding, setAdding]     = useState(false);
  const [editing, setEditing]   = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    api.get(`/employees/${employeeId}/comments`)
      .then(r => setComments(r.data))
      .catch(() => toast.error('Failed to load comments'))
      .finally(() => setLoading(false));
  }, [employeeId]);

  const handleSave = async ({ date, description, decision, imageFile }) => {
    try {
      const fd = new FormData();
      fd.append('date', date);
      fd.append('description', description);
      fd.append('decision', decision);
      if (imageFile) fd.append('image', imageFile);

      if (editing?.id) {
        const { data } = await api.put(`/employees/comments/${editing.id}`, fd);
        setComments(p => p.map(c => c.id === data.id ? data : c));
        toast.success('Comment updated');
      } else {
        const { data } = await api.post(`/employees/${employeeId}/comments`, fd);
        setComments(p => [data, ...p]);
        toast.success('Comment added');
      }
      setAdding(false);
      setEditing(null);
    } catch {
      toast.error('Failed to save comment');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`/employees/comments/${id}`);
      setComments(p => p.filter(c => c.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="form-group">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label className="form-label" style={{ margin: 0 }}>Comments (good / bad history)</label>
        {!adding && !editing && (
          <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>+ Add Comment</button>
        )}
      </div>

      {(adding || editing) && (
        <CommentForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setAdding(false); setEditing(null); }}
        />
      )}

      {loading ? (
        <p style={{ color: 'var(--text3)' }}>Loading…</p>
      ) : comments.length === 0 ? (
        <p style={{ color: 'var(--text3)' }}>No comments yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {comments.map(c => (
            <div key={c.id} className="card" style={{ padding: 10, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {c.image_path && (
                <img
                  src={`https://ems.ourschool.lk/uploads/${c.image_path}`}
                  alt=""
                  style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={`badge ${c.decision === 'good' ? 'badge-green' : 'badge-red'}`}>
                    {c.decision === 'good' ? 'Good' : 'Bad'}
                  </span>
                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>
                    {c.date ? new Date(c.date).toLocaleDateString() : '—'}
                  </span>
                </div>
                {c.description && <p style={{ margin: '4px 0 0' }}>{c.description}</p>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(c)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmpModal({ initial, onClose, onSave }) {
  const [form, setForm]   = useState(initial ? {
    ...initial,
    birth_date: initial.birth_date ? initial.birth_date.split('T')[0] : '',
    blacklisted: initial.blacklisted === 1 || initial.blacklisted === true,
    stay_overnight: initial.stay_overnight === 1 || initial.stay_overnight === true,
  } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef();
  const set = k => e => setForm(p => ({
    ...p,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const photoSrc = form.photo_path
    ? `https://ems.ourschool.lk/uploads/${form.photo_path}`
    : null;

  const uploadPhoto = async (file) => {
    if (!initial?.id) {
      toast.error('Save employee first before adding photo');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await api.post(`/employees/${initial.id}/upload`, fd);
      setForm(p => ({ ...p, photo_path: data.photo_path }));
      toast.success('Photo uploaded');
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const submit = async () => {
    if (!form.job_type || !form.name.trim()) {
      toast.error('Job type and name are required'); return;
    }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h3>{initial?.id ? 'Edit Employee' : 'Add Employee'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {initial?.id && (
            <div className="form-group">
              <label className="form-label">Photo</label>
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0])}
              />
              <div
                className="image-upload-box"
                onClick={() => photoRef.current?.click()}
                style={{ cursor: uploadingPhoto ? 'wait' : 'pointer' }}
              >
                {photoSrc ? (
                  <img src={photoSrc} alt="Employee" style={{ borderRadius: 'var(--radius)' }} />
                ) : (
                  <div>{uploadingPhoto ? 'Uploading…' : 'Click to upload photo'}</div>
                )}
              </div>
            </div>
          )}
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Job Type *</label>
              <select className="form-select" value={form.job_type} onChange={set('job_type')}>
                <option value="">— Select —</option>
                {Object.entries(JOB_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" value={form.name} onChange={set('name')} placeholder="Full name" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea className="form-textarea" rows={2} value={form.address} onChange={set('address')} />
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Marital Status</label>
              <select className="form-select" value={form.married} onChange={set('married')}>
                <option value="">— Select —</option>
                <option value="yes">Married</option>
                <option value="no">Single</option>
                <option value="widowed">Widowed</option>
                <option value="divorced">Divorced</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Sex</label>
              <select className="form-select" value={form.sex} onChange={set('sex')}>
                <option value="">— Select —</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Religion</label>
              <input className="form-input" value={form.religion} onChange={set('religion')} />
            </div>
            <div className="form-group">
              <label className="form-label">Birth Date</label>
              <input className="form-input" type="date" value={form.birth_date} onChange={set('birth_date')} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" type="tel" value={form.phone} onChange={set('phone')} />
            </div>
            <div className="form-group">
              <label className="form-label">ID No</label>
              <input className="form-input" value={form.id_no} onChange={set('id_no')} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">EPF No</label>
              <input className="form-input" value={form.epf_no} onChange={set('epf_no')} />
            </div>
            <div className="form-group">
              <label className="form-label">ETF No</label>
              <input className="form-input" value={form.etf_no} onChange={set('etf_no')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Family Details</label>
            <textarea className="form-textarea" rows={3} value={form.family_details} onChange={set('family_details')} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input id="stay_overnight" type="checkbox" checked={form.stay_overnight} onChange={set('stay_overnight')} />
            <label htmlFor="stay_overnight" className="form-label" style={{ margin: 0 }}>Stays overnight at the market</label>
          </div>
          <div className="form-group">
            <label className="form-label">Working History in Economic Center</label>
            <textarea className="form-textarea" rows={3} value={form.working_history} onChange={set('working_history')} />
          </div>
          <div className="form-group">
            <label className="form-label">Working History outside</label>
            <textarea className="form-textarea" rows={3} value={form.working_history_outside} onChange={set('working_history_outside')} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input id="blacklisted" type="checkbox" checked={form.blacklisted} onChange={set('blacklisted')} />
            <label htmlFor="blacklisted" className="form-label" style={{ margin: 0 }}>Blacklisted / bad history</label>
          </div>
          <div className="form-group">
            <label className="form-label">Education</label>
            <input className="form-input" value={form.education} onChange={set('education')} />
          </div>

          {initial?.id ? (
            <EmployeeComments employeeId={initial.id} />
          ) : (
            <p style={{ color: 'var(--text3)', fontSize: 13 }}>
              Save the employee first to add comments.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || uploadingPhoto}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Employee({ member }) {
  const [rows, setRows]    = useState([]);
  const [loading, setLoad] = useState(false);
  const [modal, setModal]  = useState(null);
  const [deleting, setDel] = useState(null);

  useEffect(() => {
    if (!member?.id) return;
    setLoad(true);
    api.get(`/employees/${member.id}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load employees'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const handleSave = async form => {
    if (modal.row?.id) {
      const { data } = await api.put(`/employees/${modal.row.id}`, form);
      setRows(p => p.map(r => r.id === data.id ? data : r));
      toast.success('Updated');
    } else {
      const { data } = await api.post('/employees', { member_id: member.id, ...form });
      setRows(p => [...p, data]);
      toast.success('Added');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this employee?')) return;
    setDel(id);
    try {
      await api.delete(`/employees/${id}`);
      setRows(p => p.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
    finally { setDel(null); }
  };

  if (!member?.id) return (
    <div className="empty-state"><p>Save Personal info first to manage employees.</p></div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setModal({ row: null })}>
          + Add Employee
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p>No employees added yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 50 }}>Photo</th>
                <th>Job Type</th>
                <th>Name</th>
                <th>Phone</th>
                <th>ID No</th>
                <th>EPF</th>
                <th>ETF</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td style={{ padding: '8px 14px' }}>
                    {row.photo_path ? (
                      <img
                        src={`https://ems.ourschool.lk/uploads/${row.photo_path}`}
                        alt={row.name}
                        style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text3)', fontSize: 12, fontWeight: 600
                      }}>
                        {row.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td><span className="badge badge-blue">{JOB_LABELS[row.job_type] || row.job_type}</span></td>
                  <td>{row.name}</td>
                  <td>{row.phone || '—'}</td>
                  <td>{row.id_no || '—'}</td>
                  <td>{row.epf_no || '—'}</td>
                  <td>{row.etf_no || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ row })}>Edit</button>
                      <button className="btn btn-danger btn-sm" disabled={deleting === row.id}
                        onClick={() => handleDelete(row.id)}>
                        {deleting === row.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <EmpModal initial={modal.row} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
}